import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackRaviReviewEvent } from '../analytics/trackRaviReviewEvent';
import { fetchReviewPage, PAGE_SIZE } from '../api';
import { RaviReviewFilterValue, RaviReviewPage, RaviReviewPageInfo, RaviReviewQuery, RaviSort } from '../types';
import { resolveFeedbackApiFilter } from '../utils/resolveFeedbackFilter';

interface DoctorState {
  list: RaviReviewPage['list'];
  hasMore: boolean;
  pageInfo?: RaviReviewPageInfo;
  sort: RaviSort;
  filter: RaviReviewFilterValue;
  page: number;
}

const INITIAL_FILTER: RaviReviewFilterValue = { type: 'all' };

export const useRaviReviews = (doctorSlug: string, userId?: string) => {
  const stateBySlugRef = useRef<Record<string, DoctorState>>({});
  const [list, setList] = useState<RaviReviewPage['list']>([]);
  const [hasMore, setHasMore] = useState(false);
  const [pageInfo, setPageInfo] = useState<RaviReviewPageInfo | undefined>();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [sort, setSort] = useState<RaviSort>('default_order');
  const [filter, setFilter] = useState<RaviReviewFilterValue>(INITIAL_FILTER);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Always mirrors the list currently shown (search results included), so "load more"
  // appends to what the user sees rather than to the cached unfiltered list.
  const listRef = useRef<RaviReviewPage['list']>([]);
  const searchRef = useRef('');
  const appliedSearchRef = useRef('');
  const requestSeqRef = useRef(0);
  const lastRequestRef = useRef<{
    page: number;
    sort: RaviSort;
    filter: RaviReviewFilterValue;
    append: boolean;
  } | null>(null);

  const toQuery = useCallback(
    (nextPage: number, nextSort: RaviSort, nextFilter: RaviReviewFilterValue): RaviReviewQuery => {
      const query: RaviReviewQuery = {
        offset: (nextPage - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        filter: resolveFeedbackApiFilter(nextSort, nextFilter),
      };

      if (searchRef.current) {
        query.search = searchRef.current;
      }

      if (nextFilter.type === 'my_feedbacks' && (nextFilter.value ?? userId)) {
        query.userId = nextFilter.value ?? userId;
      } else if (nextFilter.type === 'center_id' && nextFilter.value) {
        query.centerId = nextFilter.value;
      }

      return query;
    },
    [userId],
  );

  const run = useCallback(
    async (nextPage: number, nextSort: RaviSort, nextFilter: RaviReviewFilterValue, append: boolean) => {
      if (!doctorSlug) return;

      lastRequestRef.current = { page: nextPage, sort: nextSort, filter: nextFilter, append };
      const requestSeq = ++requestSeqRef.current;
      const searchTerm = searchRef.current;
      setIsLoading(true);
      setError(null);
      const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
      try {
        const pageData = await fetchReviewPage(doctorSlug, toQuery(nextPage, nextSort, nextFilter));
        // A newer request (e.g. the user kept typing) superseded this one.
        if (requestSeq !== requestSeqRef.current) return;
        const previousList = append ? listRef.current : [];
        const nextList = append ? [...previousList, ...pageData.list] : pageData.list;
        const durationMs = Math.round(
          (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
        );

        listRef.current = nextList;
        setList(nextList);
        setHasMore(pageData.hasMore);
        setPageInfo(pageData.pageInfo);
        setPage(nextPage);
        setSort(nextSort);
        setFilter(nextFilter);

        // Search results are not cached: the cache holds the doctor's unfiltered list.
        if (!searchTerm) {
          stateBySlugRef.current[doctorSlug] = {
            list: nextList,
            hasMore: pageData.hasMore,
            pageInfo: pageData.pageInfo,
            sort: nextSort,
            filter: nextFilter,
            page: nextPage,
          };
        }

        void trackRaviReviewEvent({
          event_type: 'review_list_load',
          slug: doctorSlug,
          page: nextPage,
          sort: nextSort,
          filter: nextFilter.type,
          duration_ms: durationMs,
          count: pageData.list.length,
          ok: true,
        });
      } catch {
        if (requestSeq !== requestSeqRef.current) return;
        const durationMs = Math.round(
          (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
        );
        setError('خطا در دریافت نظرات');
        void trackRaviReviewEvent({
          event_type: 'review_list_error',
          slug: doctorSlug,
          page: nextPage,
          sort: nextSort,
          filter: nextFilter.type,
          duration_ms: durationMs,
          ok: false,
          error_code: 'REVIEW_LIST_ERROR',
        });
      } finally {
        if (requestSeq === requestSeqRef.current) setIsLoading(false);
      }
    },
    [doctorSlug, toQuery],
  );

  const retry = useCallback(() => {
    const last = lastRequestRef.current;
    if (!last) {
      run(1, sort, filter, false);
      return;
    }
    run(last.page, last.sort, last.filter, last.append);
  }, [filter, run, sort]);

  const applyDebouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
        setIsSearchPending(false);
      }, 250),
    [],
  );

  useEffect(() => () => applyDebouncedSearch.cancel(), [applyDebouncedSearch]);

  useEffect(() => {
    if (!doctorSlug) return;

    applyDebouncedSearch.cancel();
    setSearch('');
    setDebouncedSearch('');
    setIsSearchPending(false);
    searchRef.current = '';
    appliedSearchRef.current = '';

    const cached = stateBySlugRef.current[doctorSlug];
    if (cached) {
      listRef.current = cached.list;
      setList(cached.list);
      setHasMore(cached.hasMore);
      setPageInfo(cached.pageInfo);
      setSort(cached.sort);
      setFilter(cached.filter);
      setPage(cached.page);
      return;
    }

    run(1, 'default_order', INITIAL_FILTER, false);
  }, [doctorSlug, run, applyDebouncedSearch]);

  // Re-query the server when the (debounced) search term changes.
  useEffect(() => {
    if (!doctorSlug || debouncedSearch === appliedSearchRef.current) return;
    appliedSearchRef.current = debouncedSearch;
    searchRef.current = debouncedSearch.trim();
    run(1, sort, filter, false);
    // sort/filter are read at call time on purpose: only a new term should trigger a fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) return;
    run(page + 1, sort, filter, true);
  }, [filter, hasMore, isLoading, page, run, sort]);

  const onSearch = useCallback(
    (value: string) => {
      setSearch(value);
      setIsSearchPending(true);
      applyDebouncedSearch(value);
    },
    [applyDebouncedSearch],
  );

  const onSort = useCallback(
    (value: RaviSort) => {
      applyDebouncedSearch.cancel();
      setIsSearchPending(false);
      setSort(value);
      void trackRaviReviewEvent({
        event_type: 'review_sort_change',
        slug: doctorSlug,
        page: 1,
        sort: value,
        filter: filter.type,
        ok: true,
      });
      run(1, value, filter, false);
    },
    [applyDebouncedSearch, doctorSlug, filter, run],
  );

  const onFilter = useCallback(
    (value: RaviReviewFilterValue) => {
      applyDebouncedSearch.cancel();
      setIsSearchPending(false);
      setFilter(value);
      void trackRaviReviewEvent({
        event_type: 'review_filter_change',
        slug: doctorSlug,
        page: 1,
        sort,
        filter: value.type,
        ok: true,
      });
      run(1, sort, value, false);
    },
    [applyDebouncedSearch, doctorSlug, run, sort],
  );

  const syncList = useCallback(
    (
      updater:
        | RaviReviewPage['list']
        | ((previous: RaviReviewPage['list']) => RaviReviewPage['list']),
    ) => {
      setList(previous => {
        const nextList = typeof updater === 'function' ? updater(previous) : updater;
        listRef.current = nextList;
        const cached = stateBySlugRef.current[doctorSlug];
        if (cached) {
          stateBySlugRef.current[doctorSlug] = { ...cached, list: nextList };
        }
        return nextList;
      });
    },
    [doctorSlug],
  );

  const removeReview = useCallback(
    (reviewId: string) => {
      syncList(previous => previous.filter(item => String(item.id ?? item.Id ?? '') !== reviewId));
    },
    [syncList],
  );

  const updateReviewDescription = useCallback(
    (reviewId: string, description: string) => {
      syncList(previous =>
        previous.map(item => {
          const id = String(item.id ?? item.Id ?? '');
          return id === reviewId ? { ...item, description } : item;
        }),
      );
    },
    [syncList],
  );

  return {
    list,
    hasMore,
    page,
    isLoading,
    isSearchPending,
    error,
    search,
    debouncedSearch,
    sort,
    filter,
    loadMore,
    retry,
    onSearch,
    onSort,
    onFilter,
    removeReview,
    updateReviewDescription,
  };
};
